import { useState, useEffect } from "react"
import {
  Calendar,
  Mail,
  X,
  Send,
  AlertCircle,
  Users,
  List,
  ChevronDown,
  Loader2,
  User,
  Zap,
  RefreshCw,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthProvider"
import { useUser } from "../contexts/UserContext"
import JobListingService from "../services/JobListingService"
import CustomTimePicker from "./CustomTimePicker.jsx"
import meetIcon from "../assets/icons/meet.png"
import teamsIcon from "../assets/icons/teams.png"
import falconIcon from "../assets/icons/falcon.png"

// ── Design tokens (matching InterviewsTab) ────────────────────────────────────
const FF     = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO   = "'IBM Plex Mono', monospace"
const EUCLID = "'Euclid Circular A', sans-serif"

const T = {
  border:       "0.5px solid #e5e7eb",
  borderHover:  "0.5px solid #d1d5db",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgPage:       "#f5f5f0",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

// ── Shared style helpers ──────────────────────────────────────────────────────
const Label = ({ children }) => (
  <span style={{
    display: "block",
    fontSize: 12, fontWeight: 600,
    letterSpacing: "0.07em",
    color: T.textTertiary, fontFamily: EUCLID, marginBottom: 6,
  }}>
    {children}
  </span>
)

const inputStyle = {
  width: "100%", height: 36,
  padding: "0 12px",
  border: T.border, borderRadius: T.radius,
  fontSize: 13, fontWeight: 500,
  color: T.text, fontFamily: EUCLID,
  background: T.bg, outline: "none",
  transition: "border-color 0.12s",
  boxSizing: "border-box",
}

const Input = ({ icon: Icon, error, style: extraStyle, ...props }) => (
  <div style={{ position: "relative" }}>
    {Icon && (
      <div style={{
        position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none", display: "flex", alignItems: "center",
      }}>
        <Icon size={13} color="#d1d5db" />
      </div>
    )}
    <input
      {...props}
      style={{
        ...inputStyle,
        ...(Icon ? { paddingLeft: 32 } : {}),
        ...(error ? { borderColor: "#fca5a5" } : {}),
        ...extraStyle,
      }}
      onFocus={(e) => { e.target.style.borderColor = "#9ca3af"; props.onFocus?.(e) }}
      onBlur={(e)  => { e.target.style.borderColor = error ? "#fca5a5" : "#e5e7eb"; props.onBlur?.(e) }}
    />
  </div>
)

const FieldError = ({ children }) => (
  <p style={{
    display: "flex", alignItems: "center", gap: 4,
    fontSize: 11, fontWeight: 600, color: "#ef4444",
    marginTop: 5, fontFamily: EUCLID,
  }}>
    <AlertCircle size={11} /> {children}
  </p>
)

// ── Main component ────────────────────────────────────────────────────────────
const OrgScheduleInterviewForm = ({
  isOpen,
  onClose,
  meetingId,
  onScheduleComplete,
  selectedDate,
  selectedTime,
  selectedEndTime,
  organizationData,
  interviewersData,
  candidateInfo = {},
  preselectedJobListing = null,
  onAddRound,
}) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { userData } = useUser()

  const googleLinked = !!(userData?.google_calendar?.connected)
  const msLinked     = !!(userData?.ms_calendar?.connected)

  const [organization, setOrganization] = useState(null)
  const [orgMembers, setOrgMembers] = useState([])
  const [jobListings, setJobListings] = useState([])
  const [isLoadingJobListings, setIsLoadingJobListings] = useState(false)
  const [selectedJobListingId, setSelectedJobListingId] = useState("")
  const [roundId, setRoundId] = useState("")

  const [provider, setProvider] = useState("falconhq-human-led")
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false)

  const interviewMode = provider === "falconhq-ai-led" ? "ai" : provider === "falconhq-human-led" ? "human" : ""

  const [candidateEmail, setCandidateEmail] = useState("")
  const [applicantId, setApplicantId] = useState("")
  const [hrEmail, setHREmail] = useState("")
  const [candidateName, setCandidateName] = useState("")
  const [interviewerId, setInterviewerId] = useState("")
  const [interviewerEmail, setInterviewerEmail] = useState("")
  const [interviewDate, setInterviewDate] = useState(selectedDate || "")
  const [interviewTime, setInterviewTime] = useState(selectedTime || "")
  const [interviewEndTime, setInterviewEndTime] = useState(selectedEndTime || "")
  const [interviewTitle, setInterviewTitle] = useState("")
  const [interviewDescription, setInterviewDescription] = useState("")
  const [positionTitle, setPositionTitle] = useState("")

  const [candidateEmailError, setCandidateEmailError] = useState(false)
  const [hrEmailError, setHREmailError] = useState(false)
  const [formError, setFormError] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [schedulingSuccess, setSchedulingSuccess] = useState(false)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredJobListings, setFilteredJobListings] = useState([])

  const [isInterviewerDropdownOpen, setIsInterviewerDropdownOpen] = useState(false)
  const [interviewerSearchTerm, setInterviewerSearchTerm] = useState("")
  const [filteredInterviewers, setFilteredInterviewers] = useState([])

  const [startTimeDisplay, setStartTimeDisplay] = useState("")
  const [endTimeDisplay, setEndTimeDisplay] = useState("")
  const [isRefreshing, setIsRefreshing] = useState(false)

  // ── Time helpers ──────────────────────────────────────────────────────────
  const convertTo24Hour = (timeString) => {
    if (!timeString) return ""
    const [time, period] = timeString.split(" ")
    const [hours, minutes] = time.split(":").map(Number)
    let hour24 = hours
    if (period === "PM" && hours !== 12) hour24 += 12
    else if (period === "AM" && hours === 12) hour24 = 0
    return `${hour24.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
  }

  const addMinutesToTime = (timeString, minutesToAdd) => {
    if (!timeString) return ""
    const [time, period] = timeString.split(" ")
    const [hours, minutes] = time.split(":").map(Number)
    let hour24 = hours
    if (period === "PM" && hours !== 12) hour24 += 12
    else if (period === "AM" && hours === 12) hour24 = 0
    const date = new Date()
    date.setHours(hour24, minutes, 0, 0)
    date.setMinutes(date.getMinutes() + minutesToAdd)
    const newHours = date.getHours()
    const newMinutes = date.getMinutes()
    const newPeriod = newHours >= 12 ? "PM" : "AM"
    const displayHours = newHours === 0 ? 12 : newHours > 12 ? newHours - 12 : newHours
    return `${displayHours.toString().padStart(2, "0")}:${newMinutes.toString().padStart(2, "0")} ${newPeriod}`
  }

  const convert24HourTo12Hour = (time24) => {
    if (!time24) return ""
    const [hours, minutes] = time24.split(":").map(Number)
    const period = hours >= 12 ? "PM" : "AM"
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    return `${displayHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`
  }

  const formatTimeForDisplay = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(":").map(Number)
      let period = "AM"
      let displayHours = hours
      if (hours >= 12) { period = "PM"; displayHours = hours === 12 ? 12 : hours - 12 }
      if (hours === 0) displayHours = 12
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`
    } catch { return timeString }
  }

  const handleStartTimeChange = (time24, timeDisplay) => {
    setStartTimeDisplay(timeDisplay)
    setInterviewTime(time24)
    const newEndTime = addMinutesToTime(timeDisplay, 60)
    setEndTimeDisplay(newEndTime)
    setInterviewEndTime(convertTo24Hour(newEndTime))
  }

  const handleEndTimeChange = (time24, timeDisplay) => {
    setEndTimeDisplay(timeDisplay)
    setInterviewEndTime(time24)
  }

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!searchTerm.trim()) setFilteredJobListings(jobListings)
    else setFilteredJobListings(
      jobListings.filter((l) =>
        l.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.department.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    )
  }, [jobListings, searchTerm])

  useEffect(() => {
    if (!interviewerSearchTerm.trim()) setFilteredInterviewers(orgMembers)
    else setFilteredInterviewers(
      orgMembers.filter((m) =>
        m.email.toLowerCase().includes(interviewerSearchTerm.toLowerCase()) ||
        (m.name && m.name.toLowerCase().includes(interviewerSearchTerm.toLowerCase())),
      ),
    )
  }, [orgMembers, interviewerSearchTerm])

  useEffect(() => {
    if (candidateInfo && isOpen) {
      if (candidateInfo.email) setCandidateEmail(candidateInfo.email)
      if (candidateInfo.name) setCandidateName(candidateInfo.name)
      if (candidateInfo.applicantId) setApplicantId(candidateInfo.applicantId)
    }
  }, [candidateInfo, isOpen])

  useEffect(() => {
    if (interviewMode === "ai") {
      setInterviewerId("ai-agent")
      setInterviewerEmail("ai-agent@resolut.co.in")
    } else {
      if (orgMembers.some((m) => m.uid === user?.uid)) {
        setInterviewerId(user.uid)
        setInterviewerEmail(user.email)
      } else if (orgMembers.length > 0) {
        setInterviewerId(orgMembers[0].uid)
        setInterviewerEmail(orgMembers[0].email)
      }
    }
  }, [provider, orgMembers, user])

  useEffect(() => {
    const setupFormData = async () => {
      if (!user?.uid || !isOpen || !organizationData || !interviewersData.length) return
      setOrganization(organizationData)
      setOrgMembers(interviewersData)
      if (preselectedJobListing) {
        setSelectedJobListingId(preselectedJobListing.id)
        setRoundId(preselectedJobListing.roundId)
        setPositionTitle(preselectedJobListing.role)
        setJobListings([preselectedJobListing])
        setHREmail(preselectedJobListing.recruiterEmail || "")
      }
      setIsLoadingJobListings(false)
    }
    setupFormData()
  }, [user, isOpen, organizationData, interviewersData, preselectedJobListing])

  useEffect(() => {
    if (!isOpen) {
      setCandidateEmail(""); setCandidateName(""); setInterviewDate("")
      setInterviewTime(""); setInterviewEndTime(""); setStartTimeDisplay("")
      setEndTimeDisplay(""); setInterviewTitle(""); setInterviewDescription("")
      setPositionTitle(""); setSelectedJobListingId(""); setCandidateEmailError(false)
      setHREmailError(false); setFormError(null); setSchedulingSuccess(false)
      setIsInterviewerDropdownOpen(false); setInterviewerSearchTerm("")
      setIsDropdownOpen(false); setSearchTerm(""); setJobListings("")
      setHREmail(""); setProvider("falconhq-human-led"); setIsProviderDropdownOpen(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (selectedJobListingId) {
      const sel = jobListings.find((l) => l.id === selectedJobListingId)
      if (sel) {
        setPositionTitle(sel.role)
        const nameForTitle = candidateName || candidateEmail.split("@")[0] || "Candidate"
        setInterviewTitle(`${sel.role} Interview with ${nameForTitle}`)
        if (!interviewDescription)
          setInterviewDescription(`Interview for the ${sel.role} position in the ${sel.department} department.`)
        setHREmail(sel.recruiterEmail || "")
      }
    }
  }, [selectedJobListingId, jobListings, candidateName, candidateEmail])

  useEffect(() => {
    if (selectedDate) setInterviewDate(selectedDate)
    if (selectedTime) { setInterviewTime(selectedTime); setStartTimeDisplay(convert24HourTo12Hour(selectedTime)) }
    if (selectedEndTime) { setInterviewEndTime(selectedEndTime); setEndTimeDisplay(convert24HourTo12Hour(selectedEndTime)) }
  }, [selectedDate, selectedTime, selectedEndTime])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDropdownToggle = async () => {
    setIsDropdownOpen(!isDropdownOpen)
    if (!isDropdownOpen && jobListings.length === 0 && !isLoadingJobListings) {
      setIsLoadingJobListings(true)
      try {
        const listings = await JobListingService.getOrganizationJobListings(organizationData.id)
        setJobListings(listings)
      } catch { setFormError("Failed to load job listings.") }
      finally { setIsLoadingJobListings(false) }
    }
  }

  const handleRefreshStructure = async () => {
    if (!selectedJobListingId || isRefreshing) return
    setIsRefreshing(true)
    try {
      const listings = await JobListingService.getOrganizationJobListings(organizationData.id)
      setJobListings(listings)
    } catch { /* silently fail */ }
    finally { setIsRefreshing(false) }
  }

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const handleInterviewerChange = (interviewer) => {
    setInterviewerId(interviewer.uid)
    setInterviewerEmail(interviewer.email)
    setIsInterviewerDropdownOpen(false)
    setInterviewerSearchTerm("")
  }

  const handleJobListingChange = (listing) => {
    const rid = listing?.structure?.rounds?.[0]?.id || "round-1"
    setRoundId(rid)
    setSelectedJobListingId(listing.id)
    setIsDropdownOpen(false)
    setSearchTerm("")
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedJobListingId) { setFormError("Please select a job listing."); return }
    if (!validateEmail(candidateEmail)) { setCandidateEmailError(true); return }
    if (hrEmail && !validateEmail(hrEmail)) { setHREmailError(true); return }

    setIsSaving(true)
    setFormError(null)
    try {
      const response = await fetch("https://hire100x-e1dd4.uc.r.appspot.com/interviews/schedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organizationId: organization.id,
          organizationName: organization.name,
          candidateEmail,
          candidateName,
          applicantId,
          interviewerEmail,
          interviewerId,
          interviewDate,
          interviewTime,
          interviewEndTime,
          positionTitle,
          interviewTitle: interviewTitle || `Interview for ${positionTitle || "Position"}`,
          interviewDescription,
          jobListingId: selectedJobListingId,
          roundId,
          provider,
          interviewMode,
          scheduledBy: {
            uid: user.uid,
            email: user.email,
            name: user.name || user.email.split("@")[0],
          },
          hrEmail,
          meetingId: meetingId || null,
        }),
      })
      const data = await response.json()
      if (!data.success) throw new Error(data.error || "Failed to schedule interview")

      onClose()
      if (onScheduleComplete) onScheduleComplete(data.meetingId)
    } catch (error) {
      console.error("Error scheduling interview:", error)
      setFormError("Failed to schedule interview. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null
  const today = new Date().toISOString().split("T")[0]

  const selectedJobListing  = jobListings.find((l) => l.id === selectedJobListingId)
  const selectedInterviewer = orgMembers.find((m) => m.uid === interviewerId)

  const requiredRoundType = interviewMode === "ai" ? "Ai" : "Human+Ai"
  const rounds = selectedJobListing?.interviewStructure?.rounds || []
  const hasRequiredRound = rounds.some((r) => r.roundId === "round-1" && r.roundType === requiredRoundType)
  const showRoundWarning = selectedJobListingId && !hasRequiredRound

  // ── Dropdown button shared style ──────────────────────────────────────────
  const dropdownBtnStyle = {
    width: "100%", height: 36, display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 8, padding: "0 10px 0 10px",
    border: T.border, borderRadius: T.radius,
    background: T.bg, cursor: "pointer", outline: "none",
    transition: "border-color 0.12s", boxSizing: "border-box",
    fontFamily: EUCLID,
  }

  const dropdownPanelStyle = {
    position: "absolute", top: "calc(100% + 4px)", left: 0,
    width: "100%", zIndex: 50,
    background: T.bg, border: T.border, borderRadius: T.radiusCard,
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)", overflow: "hidden",
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .osif-scroll::-webkit-scrollbar { display: none; }
        .osif-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .osif-input:focus { border-color: #9ca3af !important; }
      `}</style>

      {/* Backdrop */}
      <div style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.35)",
        backdropFilter: "blur(2px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 50, fontFamily: EUCLID,
      }}>
        <div style={{
          background: T.bg,
          width: "100%", maxWidth: 600,
          borderRadius: T.radiusCard,
          border: T.border,
          boxShadow: "0 20px 48px rgba(0,0,0,0.14)",
          display: "flex", flexDirection: "column",
          maxHeight: "92vh", overflow: "hidden",
        }}>

          {/* ── Header ───────────────────────────────────────────────────── */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "13px 20px",
            borderBottom: T.border,
            background: T.bgMuted,
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#111827", flexShrink: 0 }} />
              <div>
                <span style={{
                  fontSize: 14, fontWeight: 500,
                  color: T.text, fontFamily: EUCLID,
                }}>
                  Schedule Interview
                </span>
                {interviewTitle && (
                  <p style={{
                    margin: "2px 0 0", fontSize: 12, fontWeight: 500,
                    color: T.textTertiary, fontFamily: EUCLID,
                  }}>
                    {interviewTitle}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 28, height: 28,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: T.bg, border: T.border,
                borderRadius: T.radius - 1, cursor: "pointer",
                color: T.textTertiary, transition: "all 0.12s", outline: "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#111827"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#111827" }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.textTertiary; e.currentTarget.style.borderColor = "#e5e7eb" }}
            >
              <X size={13} strokeWidth={2} />
            </button>
          </div>

          {/* ── Form body ─────────────────────────────────────────────────── */}
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
          >
            <div
              className="osif-scroll"
              style={{ padding: "20px 20px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 18 }}
            >

              {/* Error banner */}
              {formError && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 14px",
                  background: "#fef2f2", border: "0.5px solid #fecaca",
                  borderRadius: T.radius,
                  fontSize: 12, fontWeight: 600, color: "#dc2626", fontFamily: EUCLID,
                }}>
                  <AlertCircle size={13} style={{ flexShrink: 0 }} />
                  {formError}
                </div>
              )}

              {/* ── Provider ── */}
              {(() => {
                const ProviderIcon = ({ value, size = 18 }) => {
                  if (value === "google-meet") return <img src={meetIcon} width={size} height={size} style={{ objectFit: "contain", flexShrink: 0 }} />
                  if (value === "microsoft-teams") return <img src={teamsIcon} width={size} height={size} style={{ objectFit: "contain", flexShrink: 0 }} />
                  if (value === "falconhq-human-led" || value === "falconhq-ai-led") return <img src={falconIcon} width={size} height={size} style={{ objectFit: "contain", flexShrink: 0 }} />
                  return null
                }

                const providers = [
                  { value: "falconhq-human-led", label: "Falconhq (Human + AI Co-pilot)", linked: true, disabled: false },
                  { value: "falconhq-ai-led",    label: "Falconhq (AI Interviews)",        linked: true, disabled: false },
                  { value: "google-meet",        label: "Google Meet",          linked: googleLinked, disabled: true },
                  { value: "microsoft-teams",    label: "Microsoft Teams",      linked: msLinked,     disabled: true  },
                ]
                const selected = providers.find(p => p.value === provider)
                return (
                  <div>
                    <Label>Provider</Label>
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        onClick={() => setIsProviderDropdownOpen(v => !v)}
                        style={dropdownBtnStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                          <ProviderIcon value={provider} size={18} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{selected?.label}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                            color: selected?.linked ? "#059669" : "#2563eb", fontFamily: EUCLID,
                            border: `0.5px solid ${selected?.linked ? "#05996940" : "#2563eb40"}`,
                            borderRadius: 4, padding: "1px 6px",
                            background: selected?.linked ? "#05996912" : "#2563eb12",
                            flexShrink: 0,
                          }}>{selected?.linked ? "Linked" : "Link"}</span>
                        </div>
                        <ChevronDown size={12} color={T.textTertiary} strokeWidth={2.5}
                          style={{ flexShrink: 0, transform: isProviderDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
                        />
                      </button>

                      {isProviderDropdownOpen && (
                        <div style={dropdownPanelStyle}>
                          {providers.map((opt) => {
                            const sel = provider === opt.value
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                  if (!opt.disabled) { setProvider(opt.value); setIsProviderDropdownOpen(false) }
                                  else { setIsProviderDropdownOpen(false); navigate("/settings") }
                                }}
                                style={{
                                  width: "100%", padding: "9px 14px",
                                  border: "none", borderBottom: T.border,
                                  background: sel ? "#f9f9f7" : "transparent",
                                  cursor: "pointer", outline: "none",
                                  textAlign: "left", fontFamily: EUCLID,
                                  display: "flex", alignItems: "center", justifyContent: "space-between",
                                  transition: "background 0.1s",
                                  opacity: opt.disabled ? 0.55 : 1,
                                }}
                                onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = T.bgMuted }}
                                onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent" }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <ProviderIcon value={opt.value} size={18} />
                                  <span style={{ fontSize: 13, fontWeight: sel ? 600 : 500, color: T.text }}>{opt.label}</span>
                                </div>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                                  color: opt.linked ? "#059669" : "#2563eb", fontFamily: EUCLID,
                                  border: `0.5px solid ${opt.linked ? "#05996940" : "#2563eb40"}`,
                                  borderRadius: 4, padding: "1px 6px",
                                  background: opt.linked ? "#05996912" : "#2563eb12",
                                  textDecoration: opt.linked ? "none" : "underline",
                                  flexShrink: 0,
                                }}>{opt.linked ? "Linked" : "Link"}</span>
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                    {isProviderDropdownOpen && <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setIsProviderDropdownOpen(false)} />}
                  </div>
                )
              })()}

              {/* ── Job Listing + Interviewer ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

                {/* Job Listing */}
                <div>
                  <Label>Job Listing</Label>
                  <div style={{ position: "relative" }}>
                    <button
                      type="button"
                      onClick={handleDropdownToggle}
                      style={dropdownBtnStyle}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, flex: 1 }}>
                        <List size={13} color="#d1d5db" style={{ flexShrink: 0 }} />
                        {selectedJobListing ? (
                          <span style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {selectedJobListing.role}
                            <span style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, marginLeft: 5 }}>
                              — {selectedJobListing.department}
                            </span>
                          </span>
                        ) : (
                          <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary }}>Select a listing</span>
                        )}
                      </div>
                      <ChevronDown
                        size={12} color={T.textTertiary} strokeWidth={2.5}
                        style={{ flexShrink: 0, transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div style={dropdownPanelStyle}>
                        <div style={{ maxHeight: 200, overflowY: "auto" }}>
                          {isLoadingJobListings ? (
                            <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID }}>
                              <Loader2 size={12} style={{ animation: "osif-spin 0.8s linear infinite" }} /> Loading listings…
                            </div>
                          ) : filteredJobListings.length === 0 ? (
                            <div style={{ padding: "12px 14px", fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID }}>
                              {searchTerm ? "No listings found" : "No listings available"}
                            </div>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => handleJobListingChange({ id: "" })}
                                style={{
                                  width: "100%", padding: "8px 14px", border: "none",
                                  borderBottom: T.border, background: "transparent",
                                  fontSize: 11, fontWeight: 700,                                   letterSpacing: "0.07em", color: T.textTertiary,
                                  cursor: "pointer", outline: "none", fontFamily: EUCLID, textAlign: "left",
                                  transition: "background 0.1s",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = T.bgMuted }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
                              >
                                Clear selection
                              </button>
                              {filteredJobListings.map((listing) => {
                                const sel = selectedJobListingId === listing.id
                                return (
                                  <button
                                    key={listing.id}
                                    type="button"
                                    onClick={() => handleJobListingChange(listing)}
                                    style={{
                                      width: "100%", padding: "9px 14px",
                                      border: "none", borderBottom: T.border,
                                      background: sel ? "#f9f9f7" : "transparent",
                                      cursor: "pointer", outline: "none",
                                      textAlign: "left", fontFamily: EUCLID,
                                      transition: "background 0.1s",
                                    }}
                                    onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = T.bgMuted }}
                                    onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent" }}
                                  >
                                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{listing.role}</span>
                                      <span style={{
                                        fontSize: 10, fontWeight: 700,
                                        letterSpacing: "0.07em",
                                        color: T.textTertiary, fontFamily: EUCLID,
                                        border: T.border, borderRadius: 4, padding: "1px 5px",
                                        flexShrink: 0,
                                      }}>
                                        {listing.department}
                                      </span>
                                    </div>
                                  </button>
                                )
                              })}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {isDropdownOpen && <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setIsDropdownOpen(false)} />}
                </div>

                {/* Interviewer */}
                <div>
                  <Label>Interviewer</Label>
                  {interviewMode === "ai" ? (
                    <div style={{
                      ...dropdownBtnStyle, cursor: "not-allowed",
                      background: T.bgMuted, justifyContent: "flex-start", gap: 7,
                    }}>
                      <Zap size={13} color="#d1d5db" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        ai-agent@resolut.co.in
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 700,                         letterSpacing: "0.07em", color: T.textTertiary,
                        border: T.border, borderRadius: 4, padding: "2px 6px",
                        fontFamily: EUCLID, flexShrink: 0,
                      }}>
                        AI
                      </span>
                    </div>
                  ) : (
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        onClick={() => setIsInterviewerDropdownOpen(!isInterviewerDropdownOpen)}
                        style={dropdownBtnStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, minWidth: 0 }}>
                          <Users size={13} color="#d1d5db" style={{ flexShrink: 0 }} />
                          {selectedInterviewer ? (
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {selectedInterviewer.email}
                              {selectedInterviewer.role === "admin" && (
                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: T.textTertiary }}>
                                  Admin
                                </span>
                              )}
                            </span>
                          ) : (
                            <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary }}>Select interviewer</span>
                          )}
                        </div>
                        <ChevronDown
                          size={12} color={T.textTertiary} strokeWidth={2.5}
                          style={{ flexShrink: 0, transform: isInterviewerDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
                        />
                      </button>

                      {isInterviewerDropdownOpen && (
                        <div style={dropdownPanelStyle}>
                          <div style={{ maxHeight: 200, overflowY: "auto" }}>
                            {orgMembers.length === 0 ? (
                              <div style={{ padding: "12px 14px", fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID }}>No interviewers available</div>
                            ) : filteredInterviewers.length === 0 ? (
                              <div style={{ padding: "12px 14px", fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID }}>No interviewers found</div>
                            ) : filteredInterviewers.map((member) => {
                              const sel = interviewerId === member.uid
                              return (
                                <button
                                  key={member.uid}
                                  type="button"
                                  onClick={() => handleInterviewerChange(member)}
                                  style={{
                                    width: "100%", padding: "9px 14px",
                                    border: "none", borderBottom: T.border,
                                    background: sel ? "#f9f9f7" : "transparent",
                                    cursor: "pointer", outline: "none",
                                    textAlign: "left", fontFamily: EUCLID,
                                    transition: "background 0.1s",
                                  }}
                                  onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = T.bgMuted }}
                                  onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent" }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{member.email}</span>
                                    {member.role === "admin" && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700,
                                        letterSpacing: "0.07em",
                                        color: T.textTertiary, fontFamily: EUCLID,
                                        border: T.border, borderRadius: 4, padding: "1px 5px",
                                        flexShrink: 0,
                                      }}>
                                        Admin
                                      </span>
                                    )}
                                  </div>
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {isInterviewerDropdownOpen && <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setIsInterviewerDropdownOpen(false)} />}
                </div>
              </div>

              {/* ── Round structure warning ── */}
              {showRoundWarning && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "9px 12px",
                  background: "#fffbeb", border: "0.5px solid #fde68a",
                  borderRadius: T.radius,
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "#d97706", flexShrink: 0 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "#92400e", fontFamily: EUCLID, flex: 1 }}>
                    Round Structure Not Defined (Round 1)
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    <a
                      href={interviewMode === "ai"
                        ? `/jobs/${organization?.id}/${selectedJobListingId}/add-ai-interview`
                        : `/jobs/${organization?.id}/${selectedJobListingId}/add-structure`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "#4f46e5", textDecoration: "none", fontFamily: EUCLID }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add
                    </a>
                    <button
                      type="button"
                      onClick={handleRefreshStructure}
                      disabled={isRefreshing}
                      style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: T.textTertiary, border: "none", background: "none", cursor: "pointer", fontFamily: EUCLID, outline: "none", opacity: isRefreshing ? 0.4 : 1 }}
                      title="Refresh after updating in new tab"
                    >
                      <RefreshCw size={11} style={{ animation: isRefreshing ? "osif-spin 0.8s linear infinite" : "none" }} strokeWidth={2.5} />
                      Refresh
                    </button>
                  </div>
                </div>
              )}

              {/* ── Candidate Email ── */}
              <div>
                <Label>Candidate Email</Label>
                <Input
                  type="email"
                  icon={Mail}
                  placeholder="candidate@example.com"
                  value={candidateEmail}
                  error={candidateEmailError}
                  onChange={(e) => { setCandidateEmail(e.target.value); setCandidateEmailError(false) }}
                  required
                />
                {candidateEmailError && <FieldError>Valid email required</FieldError>}
              </div>


              {/* ── Candidate Name ── */}
              <div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
                  <Label>Candidate Name</Label>
                  <span style={{ fontSize: 11, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID }}>— optional</span>
                </div>
                <Input
                  type="text"
                  icon={User}
                  placeholder="Jane Doe"
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                />
              </div>

              {/* ── Date & Time ── */}
              {interviewMode !== "ai" && (
                <div>
                  <Label>Date & Time</Label>
                  {/* overflow:hidden clips the bottom-radius corners correctly;
                      CustomTimePicker opens UPWARD (bottom-full) so it escapes the clip */}
                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                    border: T.border, borderRadius: T.radius, overflow: "hidden",
                  }}>
                    {/* Date */}
                    <div style={{ borderRight: T.border }}>
                      <div style={{ padding: "6px 12px", borderBottom: T.border, background: T.bgMuted }}>
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", color: T.textTertiary, fontFamily: EUCLID }}>Date</span>
                      </div>
                      <div style={{ position: "relative", height: 36 }}>
                        <div style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex", alignItems: "center" }}>
                          <Calendar size={13} color="#d1d5db" />
                        </div>
                        <input
                          type="date"
                          min={today}
                          value={interviewDate}
                          onChange={(e) => setInterviewDate(e.target.value)}
                          style={{ width: "100%", height: "100%", paddingLeft: 32, paddingRight: 8, border: "none", background: T.bg, fontSize: 13, fontWeight: 500, color: T.text, fontFamily: EUCLID, outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                    </div>

                    {/* Start */}
                    <div style={{ borderRight: T.border }}>
                      <div style={{ padding: "6px 12px", borderBottom: T.border, background: T.bgMuted }}>
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", color: T.textTertiary, fontFamily: EUCLID }}>Start</span>
                      </div>
                      <div style={{ height: 36 }}>
                        <CustomTimePicker value={startTimeDisplay} onChange={handleStartTimeChange} />
                      </div>
                    </div>

                    {/* End */}
                    <div>
                      <div style={{ padding: "6px 12px", borderBottom: T.border, background: T.bgMuted }}>
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", color: T.textTertiary, fontFamily: EUCLID }}>End</span>
                      </div>
                      <div style={{ height: 36 }}>
                        <CustomTimePicker value={endTimeDisplay} onChange={handleEndTimeChange} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Footer ───────────────────────────────────────────────────── */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "13px 20px",
              borderTop: T.border, background: T.bgMuted, flexShrink: 0,
            }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: EUCLID }}>
                {{ "google-meet": "Google Meet", "microsoft-teams": "Microsoft Teams", "falconhq-human-led": "⚡ Falconhq (Human + AI Co-pilot)", "falconhq-ai-led": "⚡ Falconhq (AI Interviews)" }[provider]}
              </span>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSaving}
                  style={{
                    height: 34, padding: "0 14px",
                    border: T.border, borderRadius: T.radius,
                    background: T.bg, color: T.textMuted,
                    fontSize: 12, fontWeight: 600, fontFamily: EUCLID,
                    cursor: "pointer", outline: "none",
                    transition: "border-color 0.12s, color 0.12s",
                    opacity: isSaving ? 0.4 : 1,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.color = T.text }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = T.textMuted }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSaving || schedulingSuccess}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    height: 34, padding: "0 16px",
                    border: "none", borderRadius: T.radius,
                    background: "#111827", color: "#fff",
                    fontSize: 12, fontWeight: 600, fontFamily: EUCLID,
                    cursor: isSaving || schedulingSuccess ? "not-allowed" : "pointer",
                    outline: "none",
                    opacity: isSaving || schedulingSuccess ? 0.5 : 1,
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => { if (!isSaving && !schedulingSuccess) e.currentTarget.style.background = "#27272a" }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#111827" }}
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={12} style={{ animation: "osif-spin 0.8s linear infinite" }} />
                      Scheduling…
                    </>
                  ) : (
                    <>
                      <Send size={12} strokeWidth={2.5} />
                      Schedule & Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <style>{`@keyframes osif-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  )
}

export default OrgScheduleInterviewForm